import React, {useMemo} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import type {UserMediaStatus} from '@pexip/media';
import {
    DeniedDevices,
    MediaInputStatusFlag,
    hasInputStatus,
} from '@pexip/media';

import type {DeviceStatusInfo, InputDeviceStatusInfo} from '../types';

type InputKind = 'audio' | 'video';
type InputStatusType =
    | 'available'
    | 'denied'
    | 'device-in-use'
    | 'fallback'
    | 'granted'
    | 'initial'
    | 'invalid-constraints'
    | 'no-device'
    | 'not-supported'
    | 'overconstrained'
    | 'prompt'
    | 'ready'
    | 'requested-device-not-found'
    | 'timed-out'
    | 'unknown-error';

interface RequestedMedia {
    audio: boolean;
    video: boolean;
}

export const getInputStatusType = (
    status: UserMediaStatus,
    kind: InputKind,
): InputStatusType => {
    const has = (flag: MediaInputStatusFlag) =>
        hasInputStatus(status, kind, flag);

    if (has(MediaInputStatusFlag.DeviceInUse)) {
        return 'device-in-use';
    }
    if (has(MediaInputStatusFlag.InvalidConstraints)) {
        return 'invalid-constraints';
    }
    if (has(MediaInputStatusFlag.Overconstrained)) {
        return 'overconstrained';
    }
    if (has(MediaInputStatusFlag.NotSupported)) {
        return 'not-supported';
    }
    if (has(MediaInputStatusFlag.TimedOut)) {
        return 'timed-out';
    }
    if (has(MediaInputStatusFlag.UnknownError)) {
        return 'unknown-error';
    }
    if (has(MediaInputStatusFlag.NoDevice)) {
        return 'no-device';
    }
    if (has(MediaInputStatusFlag.RequestedDeviceNotFound)) {
        return 'requested-device-not-found';
    }
    if (has(MediaInputStatusFlag.PermissionDenied)) {
        return 'denied';
    }
    if (has(MediaInputStatusFlag.Fallback)) {
        return 'fallback';
    }
    if (has(MediaInputStatusFlag.TrackPresent)) {
        return 'ready';
    }
    if (has(MediaInputStatusFlag.PermissionPrompt)) {
        return 'prompt';
    }
    if (has(MediaInputStatusFlag.PermissionGranted)) {
        return 'granted';
    }
    if (has(MediaInputStatusFlag.DeviceAvailable)) {
        return 'available';
    }
    return 'initial';
};

const toDeniedDevice = (kind: InputKind) =>
    kind === 'audio' ? DeniedDevices.Microphone : DeniedDevices.Camera;

export const useDeviceStatusInfo = (
    status: UserMediaStatus | undefined,
    requested: RequestedMedia,
): InputDeviceStatusInfo => {
    const {t} = useTranslation();

    return useMemo(() => {
        const emptyInfo: DeviceStatusInfo = {title: '', message: ''};
        if (!status) {
            return {audio: emptyInfo, video: emptyInfo};
        }

        const promptMessage = (
            <Trans
                i18nKey="device-status.initial-permissions-not-granted.message"
                t={t}
            >
                Please select <strong>Allow</strong> in the next step when
                prompted by your browser. We will only have access for the
                duration of the meeting.
            </Trans>
        );

        const getInputInfo = (
            kind: InputKind,
            type: InputStatusType,
        ): DeviceStatusInfo => {
            const audio = kind === 'audio';
            const error = toDeniedDevice(kind);
            switch (type) {
                case 'denied':
                    return {
                        title: audio
                            ? t(
                                  'device-status.permissions-rejected-audioinput.title',
                                  'Microphone permission not granted',
                              )
                            : t(
                                  'device-status.permissions-rejected-videoinput.title',
                                  'Camera permission not granted',
                              ),
                        message: audio
                            ? t(
                                  'device-status.permissions-rejected-audioinput-only.message',
                                  'You have denied permission to use your microphone. It will not be available for this meeting',
                              )
                            : t(
                                  'device-status.permissions-rejected-videoinput-only.message',
                                  'You have denied permission to use your camera. It will not be available for this meeting',
                              ),
                        controls: 'permissions',
                        error,
                    };
                case 'device-in-use':
                    return {
                        title: audio
                            ? t(
                                  'device-status.audio-device-in-use.title',
                                  'Microphone unavailable',
                              )
                            : t(
                                  'device-status.video-device-in-use.title',
                                  'Camera unavailable',
                              ),
                        message: audio
                            ? t(
                                  'device-status.audio-device-in-use.message',
                                  'Another application (e.g. Teams or Slack) is using your microphone. Close that application to use it here.',
                              )
                            : t(
                                  'device-status.video-device-in-use.message',
                                  'Another application (e.g. Teams or Slack) is using your camera. Close that application to use it here.',
                              ),
                        controls: 'media',
                        error,
                        retriable: true,
                    };
                case 'invalid-constraints':
                    return {
                        title: audio
                            ? t(
                                  'device-status.invalid-audio-constraints.title',
                                  'Microphone is not configured properly',
                              )
                            : t(
                                  'device-status.invalid-video-constraints.title',
                                  'Camera is not configured properly',
                              ),
                        message: audio
                            ? t(
                                  'device-status.invalid-audio-constraints.message',
                                  'Try clearing your browser cache',
                              )
                            : t(
                                  'device-status.invalid-video-constraints.message',
                                  'Try clearing your browser cache',
                              ),
                        controls: 'media',
                        error,
                    };
                case 'overconstrained':
                    return {
                        title: audio
                            ? t(
                                  'device-status.audio-overconstrained.title',
                                  'Microphone is overly constrained',
                              )
                            : t(
                                  'device-status.video-overconstrained.title',
                                  'Camera is overly constrained',
                              ),
                        message: audio
                            ? t(
                                  'device-status.audio-overconstrained.message',
                                  'Try lowering your quality settings',
                              )
                            : t(
                                  'device-status.video-overconstrained.message',
                                  'Try lowering your quality settings',
                              ),
                        controls: 'media',
                        error,
                    };
                case 'not-supported':
                    return {
                        title: audio
                            ? t(
                                  'device-status.not-supported-error-only-audio.title',
                                  'Browser does not support',
                              )
                            : t(
                                  'device-status.not-supported-error-only-video.title',
                                  'Browser does not support',
                              ),
                        message: audio
                            ? t(
                                  'device-status.not-supported-error-only-audio.message',
                                  "We can't get media from your browser. Try to use another modern browser.",
                              )
                            : t(
                                  'device-status.not-supported-error-only-video.message',
                                  "We can't get media from your browser. Try to use another modern browser.",
                              ),
                        controls: 'media',
                        error,
                    };
                case 'timed-out':
                    return {
                        title: audio
                            ? t(
                                  'device-status.audio-request-timed-out.title',
                                  'Microphone request timed out',
                              )
                            : t(
                                  'device-status.video-request-timed-out.title',
                                  'Camera request timed out',
                              ),
                        message: audio
                            ? t(
                                  'device-status.audio-request-timed-out.message',
                                  'Your browser did not respond. Try again.',
                              )
                            : t(
                                  'device-status.video-request-timed-out.message',
                                  'Your browser did not respond. Try again.',
                              ),
                        controls: 'media',
                        error,
                        retriable: true,
                        retryOnly: true,
                    };
                case 'unknown-error':
                    return {
                        title: audio
                            ? t(
                                  'device-status.unknown-error-only-audioinput.title',
                                  'Microphone unavailable',
                              )
                            : t(
                                  'device-status.unknown-error-only-videoinput.title',
                                  'Camera unavailable',
                              ),
                        message: audio
                            ? t(
                                  'device-status.unknown-error-only-audioinput.message',
                                  'We are not able to use your microphone. Try to reconnect your device or use a different one',
                              )
                            : t(
                                  'device-status.unknown-error-only-videoinput.message',
                                  'We are not able to use your camera. Try to reconnect your device or use a different one',
                              ),
                        controls: 'media',
                        error,
                    };
                case 'no-device':
                    return {
                        title: audio
                            ? t(
                                  'device-status.no-audio-devices-found.title',
                                  'There’s no connected microphone',
                              )
                            : t(
                                  'device-status.no-video-devices-found.title',
                                  'There’s no connected camera',
                              ),
                        message: audio
                            ? t(
                                  'device-status.no-audio-devices-found.message',
                                  'Your microphone seems to be unavailable. Please check if it is plugged in and set up correctly',
                              )
                            : t(
                                  'device-status.no-video-devices-found.message',
                                  'Your camera seems to be unavailable. Please check if it is plugged in and set up correctly',
                              ),
                        controls: 'media',
                        error,
                    };
                case 'requested-device-not-found':
                    return {
                        title: audio
                            ? t(
                                  'device-status.audio-device-not-found.title',
                                  'We can’t find your microphone',
                              )
                            : t(
                                  'device-status.video-device-not-found.title',
                                  'We can’t find your camera',
                              ),
                        message: audio
                            ? t(
                                  'device-status.audio-device-not-found.message',
                                  'Your preferred microphone is unavailable',
                              )
                            : t(
                                  'device-status.video-device-not-found.message',
                                  'Your preferred camera is unavailable',
                              ),
                        controls: 'media',
                        error,
                    };
                case 'fallback':
                    return {
                        title: audio
                            ? t(
                                  'device-status.permissions-granted-fallback-audioinput.title',
                                  'Unable to use your preferred microphone',
                              )
                            : t(
                                  'device-status.permissions-granted-fallback-videoinput.title',
                                  'Unable to use your preferred camera',
                              ),
                        message: audio
                            ? t(
                                  'device-status.permissions-granted-fallback-audioinput.message',
                                  'We are unable to use your preferred microphone',
                              )
                            : t(
                                  'device-status.permissions-granted-fallback-videoinput.message',
                                  'We are unable to use your preferred camera',
                              ),
                        controls: 'media',
                        warning: error,
                    };
                case 'prompt':
                    return {
                        title: audio
                            ? t(
                                  'device-status.initial-permissions-not-granted.title-audioonly',
                                  'We need your permission to use your microphone',
                              )
                            : t(
                                  'device-status.initial-permissions-not-granted.title-videoonly',
                                  'We need your permission to use your camera',
                              ),
                        message: promptMessage,
                        controls: 'permissions-dismiss',
                    };
                case 'granted':
                    return {
                        title: t(
                            'device-status.initial-permissions-granted.title',
                            'Permissions granted',
                        ),
                        message: audio
                            ? t(
                                  'device-status.initial-permissions-audio-input-granted.message',
                                  'You have granted permission to use your microphone',
                              )
                            : t(
                                  'device-status.initial-permissions-video-input-granted.message',
                                  'You have granted permission to use your camera',
                              ),
                        controls: 'media',
                    };
                case 'ready':
                    return {...emptyInfo, controls: 'media'};
                case 'available':
                case 'initial':
                    return emptyInfo;
            }
        };

        return {
            audio: requested.audio
                ? getInputInfo('audio', getInputStatusType(status, 'audio'))
                : emptyInfo,
            video: requested.video
                ? getInputInfo('video', getInputStatusType(status, 'video'))
                : emptyInfo,
        };
    }, [requested.audio, requested.video, status, t]);
};

export const useRequestPermissionStatusInfo = (
    statusInfo: InputDeviceStatusInfo,
): DeviceStatusInfo => {
    const {t} = useTranslation();
    const audioPending = statusInfo.audio.controls === 'permissions-dismiss';
    const videoPending = statusInfo.video.controls === 'permissions-dismiss';

    if (statusInfo.video.error) {
        return statusInfo.video;
    }
    if (audioPending && videoPending) {
        return {
            title: t(
                'device-status.initial-permissions-not-granted.title',
                'We need your permission to use your camera and microphone',
            ),
            message: statusInfo.audio.message,
            controls: 'permissions-dismiss',
        };
    }
    if (audioPending) {
        return statusInfo.audio;
    }
    if (videoPending) {
        return statusInfo.video;
    }
    if (statusInfo.audio.error) {
        return statusInfo.audio;
    }
    if (statusInfo.audio.warning) {
        return statusInfo.audio;
    }
    return statusInfo.video;
};
